import React, { FC, useState, useEffect, useRef } from "react"
import { observer } from "mobx-react-lite"
import { 
  AppState, 
  Linking, 
  Modal, 
  View, 
  Text, 
  ScrollView, 
  ActivityIndicator, 
  Pressable, 
  StyleSheet, 

} from "react-native"
import { useStores } from "../models"
import { Ionicons, MaterialCommunityIcons } from "@expo/vector-icons"
import { navigate } from "../navigators"
import * as Storage from "../utils/storage"
import { useFirebaseMessaging } from '../services/firebaseConfig'
import backgroundTasks from "../utils/backgroundTasks"
import CustomTooltipOverlay from "../components/elements/CustomTooltipOverlay"

// Tipos de datos
export type ActionTYPE = {
  name: string;
  callback: any;
}

export type AlertDialogTYPE = {
  title: string;
  message: string;
  cancelAction: ActionTYPE;
  actions: ActionTYPE[];
}

export type HintDialogTYPE = {
  title: string;
  message: string;
  x: number | undefined;
  y: number | undefined;
}

export const StatusScreen: FC = observer(function StatusScreen() {
  const { authenticationStore, statusStore, formsStore, tasksStore, itemsStore } = useStores()
  const { firebaseMessage, firebaseMessageClean } = useFirebaseMessaging((token:string)=>setTimeout(()=>authenticationStore.updPushToken(token),10000))
  
  const { width, height } = statusStore.windowDimensions
  const [isLogVisible, setLogVisible] = useState(true)
  const [alertDialog, setAlertDialog] = useState<AlertDialogTYPE | undefined>()
  const [hintDialog, setHintDialog] = useState<HintDialogTYPE | undefined>()

  // --- EFECTO FIREBASE ---
  useEffect(() => {
    if (firebaseMessage !== undefined) {
      setAlertDialog({
        title: firebaseMessage.notification.title,
        message: firebaseMessage.notification.body,
        cancelAction: { name: "Cerrar", callback: () => { firebaseMessageClean() } },
        actions: firebaseMessage.data?.click_action === "app.task" ?
          [
            {
              name: "Sincronizar y Cerrar", 
              callback: async () => { await formsStore.getSubscriptions(); await tasksStore.getTasks(); firebaseMessageClean(); }
            },
            {
              name: "Sincronizar e ir a Tareas", 
              callback: async () => { await formsStore.getSubscriptions(); await tasksStore.getTasks(); firebaseMessageClean(); navigate("TasksList"); }
            },
          ]
          : [
            {
              name: "Sincronizar", 
              callback: async () => { Storage.removeItem("must_sync"); await itemsStore.upload({}); await formsStore.getSubscriptions(); firebaseMessageClean(); }
            }
          ]
      });
    } else {
      setAlertDialog(undefined)
    }
  }, [firebaseMessage]);

  // --- EFECTO ALERT MESSAGE ---
  useEffect(() => {
    if (statusStore.alertMessage && statusStore.alertMessage.title) {
      setAlertDialog({
        title: statusStore.alertMessage.title,
        message: statusStore.alertMessage.body,
        cancelAction: { name: "Cerrar", callback: () => { setAlertDialog(undefined); statusStore.setAlertMessage(undefined) } },
        actions: []
      });
    } else {
      setAlertDialog(undefined)
    }
  }, [statusStore.alertMessage]);

  // --- EFECTO HINT MESSAGE ---
  useEffect(() => {
    if (statusStore.hintMessage && statusStore.hintMessage.title) {
      setHintDialog({
        title: statusStore.hintMessage.title,
        message: statusStore.hintMessage.body,
        x: statusStore.hintMessage.x,
        y: statusStore.hintMessage.y,
      });
    } else {
      setHintDialog(undefined)
    }
  }, [statusStore.hintMessage]);

  // --- BACKGROUND TASKS ---
  const onBackground = async (nextState: string) => {
    if (authenticationStore.userInfo?.user) {
      await backgroundTasks(authenticationStore, itemsStore, tasksStore);
    }
  }

  // --- DEEP LINKING ---
  useEffect(() => {
    const handleDeepLink = async () => {
      const url = await Linking.getInitialURL();
      if (url) {
        const parsedLink = url.match(/field4:\/dept\/(\w+)\/form\/(\w+)/);
        if (parsedLink) {
          const [_, deptId, formId] = parsedLink;
          navigate('ItemsList', { deptId, formId });
        }
      }
    };
    const listener = AppState.addEventListener("change", onBackground);
    handleDeepLink();
    return () => {
      listener.remove();
    }
  }, []);

  return (
    <View style={styles.rootContainer} pointerEvents="box-none">
      
      {/* 1. MODAL DE CARGA / LOGS (Sustituye al Spinner y VStack de NB) */}
      <Modal transparent={true} visible={statusStore.status === "pending"} animationType="fade">
        <View style={styles.loadingOverlay}>
          <View style={styles.spinnerWrapper}>
            <ActivityIndicator size="large" color="#3700B3" />
          </View>
          
          <View style={[styles.logBox, { height: isLogVisible ? height * 0.6 : 55 }]}>
            <Pressable onPress={() => setLogVisible(!isLogVisible)} style={styles.logHeader}>
              <Text style={styles.logHeaderTitle}>
                {statusStore.loadingInfoLst.length > 0 ? statusStore.loadingInfoLst[0].title : "Procesando..."}
              </Text>
              <MaterialCommunityIcons name="message-processing-outline" size={20} color="#FFB300" />
            </Pressable>
            
            {isLogVisible && (
              <ScrollView style={styles.logContent}>
                {statusStore.loadingInfoLst.map((info, idx) => (
                  <View key={`log-info-${idx}`} style={styles.logRow}>
                    <Text style={styles.logText}>{info.title}</Text>
                    {info.spinner === true ? (
                      <ActivityIndicator size="small" color="#3700B3" />
                    ) : info.spinner === false ? (
                      <Ionicons name="checkmark-circle" size={20} color="#43A047" />
                    ) : null}
                  </View>
                ))}
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* 2. FLASH MESSAGE (Banner superior) */}
      {statusStore.flashmessage !== "" && (
        <View style={[styles.flashBanner, { width }]}>
          <Text style={styles.flashText}>{statusStore.flashmessage}</Text>
          <Pressable onPress={statusStore.clearFlashMessage} style={styles.flashCloseBtn}>
            <Ionicons name="close" size={24} color="white" />
          </Pressable>
        </View>
      )}

      {/* 3. ALERT DIALOG (Sustituye al AlertDialog de NB) */}
      {alertDialog && (
        <Modal transparent={true} visible={true} animationType="fade">
          <View style={styles.alertOverlay}>
            <View style={styles.alertContainer}>
              <Text style={styles.alertTitle}>{alertDialog.title}</Text>
              <Text style={styles.alertBody}>{alertDialog.message}</Text>
              
              <View style={styles.alertFooter}>
                <Pressable onPress={alertDialog.cancelAction.callback} style={styles.btnSecondary}>
                  <Text style={styles.btnSecondaryText}>{alertDialog.cancelAction.name}</Text>
                </Pressable>
                
                {alertDialog.actions.map((action, idx) => (
                  <Pressable key={`action-${idx}`} onPress={action.callback} style={styles.btnPrimary}>
                    <Text style={styles.btnPrimaryText}>{action.name}</Text>
                  </Pressable>
                ))}
              </View>
            </View>
          </View>
        </Modal>
      )}

      {/* 4. HINT DIALOG */}
      {hintDialog && (
        <CustomTooltipOverlay 
          isVisible={true} 
          width={width * 0.98} 
          height={height * 0.98} 
          x={hintDialog.x ?? (width * 0.98)*0.25} 
          y={hintDialog.y ?? width*0.25}
          content={hintDialog.message} 
          title={hintDialog.title}
          onClose={() => statusStore.setHintMessage("", "", undefined, undefined)}
        />
      )}
    </View>
  )
})

const styles = StyleSheet.create({
  rootContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999999,
  },
  loadingOverlay: {
    flex: 1,
    backgroundColor: "rgba(255,255,255,0.85)",
    justifyContent: "space-between",
    padding: 15,
  },
  spinnerWrapper: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logBox: {
    backgroundColor: '#F5F5F5',
    borderRadius: 6,
    elevation: 5,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
    overflow: 'hidden',
  },
  logHeader: {
    backgroundColor: '#424242',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 15,
  },
  logHeaderTitle: { color: 'white', fontWeight: 'bold', fontSize: 14 },
  logContent: { padding: 10 },
  logRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  logText: { fontSize: 13, color: '#333', flex: 1, marginRight: 10 },
  flashBanner: {
    flexDirection: "row",
    backgroundColor: "#EF5350",
    padding: 12,
    justifyContent: "space-between",
    alignItems: "center",
  },
  flashText: { color: "#FFF", fontWeight: 'bold', flex: 1 },
  flashCloseBtn: { paddingLeft: 10 },
  alertOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  alertContainer: {
    width: '100%',
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    elevation: 10,
  },
  alertTitle: { fontSize: 18, fontWeight: 'bold', marginBottom: 10, color: '#212121' },
  alertBody: { fontSize: 15, color: '#616161', marginBottom: 25, lineHeight: 22 },
  alertFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  btnSecondary: {
    paddingVertical: 10,
    paddingHorizontal: 15,
    marginRight: 10,
  },
  btnSecondaryText: { color: '#757575', fontWeight: 'bold' },
  btnPrimary: {
    backgroundColor: '#3700B3',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    marginBottom: 5,
  },
  btnPrimaryText: { color: 'white', fontWeight: 'bold' },
});